import { useState, useEffect, Fragment, useCallback } from 'react';
import {
  Download, Search, Filter, Phone, PhoneIncoming, PhoneOutgoing,
  Clock, AlertCircle, ChevronLeft, ChevronRight, RefreshCw, ChevronDown, ChevronUp,
  User, Users, Bot, GitBranch, ArrowRight, Headphones
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/voip/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';
import { useIsMobile } from '@/hooks/use-media-query';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { CDR, CallDirection, HangupDisposition } from '@/types/cdr';
import { getErrorMessage } from '../../lib/utils';


interface CDRTableProps {
  compact?: boolean;
  showFilters?: boolean;
  showExport?: boolean;
  showBackfill?: boolean;
  direction?: CallDirection | 'all';
  disposition?: HangupDisposition | 'all';
  startDate?: string;
  endDate?: string;
  className?: string;
}

const PAGE_SIZE = 50;

export default function CDRTable({
  compact = false,
  showFilters = true,
  showExport = true,
  showBackfill = false,
  direction: controlledDirection,
  disposition: controlledDisposition,
  startDate: controlledStartDate,
  endDate: controlledEndDate,
  className = '',
}: CDRTableProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [search, setSearch] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [localDirection, setLocalDirection] = useState<CallDirection | 'all'>(controlledDirection || 'all');
  const [localDisposition, setLocalDisposition] = useState<HangupDisposition | 'all'>(controlledDisposition || 'all');
  const [localStartDate, setLocalStartDate] = useState(
    controlledStartDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [localEndDate, setLocalEndDate] = useState(
    controlledEndDate || new Date().toISOString().split('T')[0]
  );

  const direction = controlledDirection !== undefined ? controlledDirection : localDirection;
  const disposition = controlledDisposition !== undefined ? controlledDisposition : localDisposition;
  const startDate = controlledStartDate !== undefined ? controlledStartDate : localStartDate;
  const endDate = controlledEndDate !== undefined ? controlledEndDate : localEndDate;

  useEffect(() => { setPage(1); }, [direction, startDate, endDate]);

  const { data, isLoading, refetch } = useQuery<{ records: CDR[]; total: number }>({
    queryKey: ['cdr-unified', page, direction, disposition, startDate, endDate, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('offset', String((page - 1) * PAGE_SIZE));
      params.set('limit', String(PAGE_SIZE));
      if (startDate) params.set('start_date', new Date(startDate).toISOString());
      if (endDate) params.set('end_date', new Date(endDate + 'T23:59:59').toISOString());
      if (direction !== 'all') params.set('call_direction', direction);
      if (disposition !== 'all') params.set('hangup_disposition', disposition);
      if (search) params.set('caller_id_number', search);
      // Task #3713 — getCDR() è tipizzato (data = CdrRecord[], pagination sibling
      // { total, limit, offset, pages } dal backend cdr.ts): niente più cast locale.
      const res = await apiClient.getCDR(Object.fromEntries(params));
      const records = res.data || [];
      const total = res.pagination?.total ?? records.length;
      return { records, total: Number(total) || 0 };
    },
  });

  const records = data?.records || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatDuration = useCallback((seconds: number) => {
    if (!seconds || seconds < 0) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }, []);

  const formatDateTime = useCallback((iso: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }, []);

  const getDirectionIcon = (dir: CallDirection) => {
    switch (dir) {
      case 'inbound': return <PhoneIncoming className="h-3.5 w-3.5 text-green-600" />;
      case 'outbound': return <PhoneOutgoing className="h-3.5 w-3.5 text-blue-600" />;
      case 'internal': return <Phone className="h-3.5 w-3.5 text-purple-600" />;
      case 'emergency': return <AlertCircle className="h-3.5 w-3.5 text-red-600" />;
      default: return <Phone className="h-3.5 w-3.5 text-gray-600" />;
    }
  };

  const getDirectionLabel = (dir: CallDirection) => {
    switch (dir) {
      case 'inbound': return 'Entrata';
      case 'outbound': return 'Uscita';
      case 'internal': return 'Interna';
      case 'emergency': return 'Emergenza';
      default: return dir;
    }
  };

  const getDispositionBadge = (disp: HangupDisposition) => {
    switch (disp) {
      case 'answered': return <Badge className="bg-green-600 text-white text-xs">Risposta</Badge>;
      case 'no_answer': return <Badge variant="secondary" className="text-xs">Non risposta</Badge>;
      case 'busy': return <Badge variant="destructive" className="text-xs">Occupato</Badge>;
      case 'failed': return <Badge variant="destructive" className="text-xs">Fallita</Badge>;
      case 'rejected': return <Badge variant="destructive" className="bg-red-700 text-xs">Rifiutata</Badge>;
      case 'cancelled': return <Badge variant="outline" className="border-orange-500 text-orange-600 text-xs">Cancellata</Badge>;
      default: return <Badge variant="outline" className="text-xs">{disp || '-'}</Badge>;
    }
  };

  const getDestIcon = (dt: string) => {
    if (['ai_agent', 'internal_ai_agent', 'external_ai_agent'].includes(dt)) return <Bot className="h-3 w-3" />;
    if (dt === 'queue') return <Headphones className="h-3 w-3" />;
    if (dt === 'ivr') return <GitBranch className="h-3 w-3" />;
    if (['ring_all', 'ring_group'].includes(dt)) return <Users className="h-3 w-3" />;
    if (dt === 'extension') return <User className="h-3 w-3" />;
    return <Phone className="h-3 w-3" />;
  };

  const getDestTypeBadge = (r: CDR) => {
    const meta = r.metadata;
    const dt = meta?.destination_type;

    if (dt) {
      const isAI = ['ai_agent', 'internal_ai_agent', 'external_ai_agent'].includes(dt);
      const cls = isAI ? 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300' :
        dt === 'queue' ? 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300' :
        dt === 'ivr' ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300' :
        ['ring_all', 'ring_group'].includes(dt) ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300' :
        'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300';
      const label = isAI ? 'AI Agent' : dt === 'queue' ? 'Coda' : dt === 'ivr' ? 'IVR' :
        dt === 'extension' ? 'Interno' : ['ring_all', 'ring_group'].includes(dt) ? 'Ring Group' : dt;
      const name = meta.destination_name || meta.destination_id || '';
      const shortInfo = dt === 'queue' && meta.answered_by_extension
        ? `→ Ext ${meta.answered_by_extension}`
        : dt === 'ivr' && meta.transfer_destination
          ? `→ ${meta.transfer_destination}`
          : '';
      return (
        <div className="flex flex-col gap-0.5">
          <Badge variant="outline" className={`${cls} w-fit text-xs flex items-center gap-1`}>
            {getDestIcon(dt)}
            {label}
          </Badge>
          {name && <span className="text-xs text-muted-foreground truncate max-w-[120px]">{name}</span>}
          {shortInfo && <span className="text-xs text-green-600 dark:text-green-400 font-medium">{shortInfo}</span>}
        </div>
      );
    }

    if (r.call_direction === 'inbound' && r.destination_number && /^\d{3,5}$/.test(r.destination_number)) {
      return (
        <div className="flex flex-col gap-0.5">
          <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 w-fit text-xs flex items-center gap-1">
            <User className="h-3 w-3" />
            Interno
          </Badge>
          <span className="text-xs text-muted-foreground">Ext {r.destination_number}</span>
        </div>
      );
    }

    if (r.call_direction === 'outbound') {
      return <span className="text-xs text-muted-foreground">—</span>;
    }

    return <span className="text-xs text-muted-foreground">-</span>;
  };

  const getMOSBadge = (mos?: number) => {
    if (!mos) return <span className="text-xs text-muted-foreground">-</span>;
    const cls = mos >= 4.0 ? 'bg-green-600' : mos >= 3.5 ? 'bg-blue-600' : mos >= 3.0 ? 'bg-yellow-600' : 'bg-red-600';
    const label = mos >= 4.0 ? 'Ottima' : mos >= 3.5 ? 'Buona' : mos >= 3.0 ? 'Discreta' : 'Scarsa';
    return <Badge className={`${cls} text-white text-xs`}>{mos.toFixed(2)} ({label})</Badge>;
  };

  const exportCDR = async (format: 'csv' | 'json') => {
    try {
      const response = await apiClient.exportCDR(format);
      const blob = new Blob([typeof response === 'string' ? response : JSON.stringify(response, null, 2)], {
        type: format === 'csv' ? 'text/csv' : 'application/json',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cdr_export_${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Export completato', description: `CDR esportati in ${format.toUpperCase()}` });
    } catch {
      toast({ title: 'Errore export', description: 'Impossibile esportare i CDR', variant: 'destructive' });
    }
  };

  const backfillMetadata = async () => {
    try {
      setIsBackfilling(true);
      const r1 = await apiClient.post<{ updated_trunk?: number; updated_gateway?: number; updated_metadata?: number }>('/cdr/backfill-metadata');
      let gatewayUpdated = 0;
      try {
        const r2 = await apiClient.post<{ updated?: number }>('/cdr/backfill-gateway');
        gatewayUpdated = r2.data!.updated! || 0;
      } catch {}
      const total = (r1.data!.updated_trunk! || 0) + (r1.data!.updated_gateway! || 0) + (r1.data!.updated_metadata! || 0) + gatewayUpdated;
      toast({ title: 'Dati CDR aggiornati', description: `${total} record aggiornati` });
      refetch();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || "Errore durante l'aggiornamento", variant: 'destructive' });
    } finally {
      setIsBackfilling(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {showFilters && (
        <Card className="glass-card">
          <CardHeader className="pb-3 cursor-pointer md:cursor-default" onClick={() => isMobile && setMobileFiltersOpen(!mobileFiltersOpen)}>
            <CardTitle className="flex items-center justify-between text-foreground text-base">
              <span className="flex items-center">
                <Filter className="h-4 w-4 mr-2 text-blue-500" />
                Filtri
              </span>
              <span className="md:hidden">
                {mobileFiltersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className={`${isMobile && !mobileFiltersOpen ? 'hidden' : ''} md:block`}>
            <div className="grid gap-3 md:grid-cols-6">
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground/80">Dal</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setLocalStartDate(e.target.value); setPage(1); }}
                  className="bg-background/50 border-input text-foreground text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground/80">Al</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setLocalEndDate(e.target.value); setPage(1); }}
                  className="bg-background/50 border-input text-foreground text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground/80">Cerca</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Numero, nome..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-8 bg-background/50 border-input text-foreground text-sm placeholder:text-muted-foreground"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground/80">Direzione</label>
                <Select value={direction} onValueChange={(v) => { setLocalDirection(v as CallDirection | 'all'); setPage(1); }}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="inbound">Entrata</SelectItem>
                    <SelectItem value="outbound">Uscita</SelectItem>
                    <SelectItem value="internal">Interne</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground/80">Esito</label>
                <Select value={disposition} onValueChange={(v) => { setLocalDisposition(v as HangupDisposition | 'all'); setPage(1); }}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="answered">Risposta</SelectItem>
                    <SelectItem value="no_answer">Non risposta</SelectItem>
                    <SelectItem value="busy">Occupato</SelectItem>
                    <SelectItem value="failed">Fallita</SelectItem>
                    <SelectItem value="cancelled">Cancellata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={() => refetch()} size="sm" className="flex-1">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Aggiorna
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="glass-card-enhanced overflow-hidden">
        <CardHeader className="border-b border-border bg-muted/20 py-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-foreground text-base">
                Registro Chiamate {total > 0 && <span className="text-muted-foreground font-normal">({total})</span>}
              </CardTitle>
              {!compact && (
                <CardDescription className="text-muted-foreground text-xs mt-0.5">
                  Dettaglio completo con trunk, destinazione interna e qualità
                </CardDescription>
              )}
            </div>
            <div className="flex gap-2">
              {showBackfill && (
                <Button variant="outline" size="sm" onClick={backfillMetadata} disabled={isBackfilling}
                  className="border-blue-500 text-blue-500 hover:bg-blue-500/10 text-xs">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isBackfilling ? 'animate-spin' : ''}`} />
                  {isBackfilling ? 'Aggiornamento...' : 'Popola Metadata'}
                </Button>
              )}
              {showExport && (
                <>
                  <Button variant="outline" size="sm" onClick={() => exportCDR('csv')}
                    className="border-green-500 text-green-500 hover:bg-green-500/10 text-xs">
                    <Download className="h-3.5 w-3.5 mr-1" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => exportCDR('json')}
                    className="border-purple-500 text-purple-500 hover:bg-purple-500/10 text-xs">
                    <Download className="h-3.5 w-3.5 mr-1" /> JSON
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
            <div className="md:hidden space-y-2 p-3">
              {records.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-10 w-10 mx-auto mb-3 opacity-20" />
                  <p>Nessun record trovato</p>
                </div>
              ) : (
                records.map((r) => (
                  <div key={r.id} className="bg-background/50 border border-border rounded-lg p-3 space-y-2" onClick={() => setExpandedRow(expandedRow === r.id ? null : r.id)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getDirectionIcon(r.call_direction)}
                        <span className="text-xs font-medium">{getDirectionLabel(r.call_direction)}</span>
                      </div>
                      {getDispositionBadge(r.hangup_disposition)}
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm font-mono">{r.caller_id_number}</div>
                        {r.caller_id_name && <div className="text-xs text-muted-foreground">{r.caller_id_name}</div>}
                      </div>
                      <span className="text-xs text-muted-foreground">→ {r.destination_number}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(r.start_time)}
                      </span>
                      <span className="font-mono bg-muted/50 px-1.5 py-0.5 rounded">{formatDuration(r.duration)}</span>
                    </div>
                    {expandedRow === r.id && (
                      <div className="pt-2 border-t border-border grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Trunk:</span> <span className="font-medium">{r.trunk_name || '-'}</span></div>
                        <div><span className="text-muted-foreground">Gateway:</span> <span className="font-medium">{r.gateway_name || '-'}</span></div>
                        <div><span className="text-muted-foreground">Codec:</span> <span className="font-medium">{r.read_codec || '-'}</span></div>
                        <div><span className="text-muted-foreground">MOS:</span> {r.mos_score ? <span className="font-medium">{r.mos_score.toFixed(2)}</span> : '-'}</div>
                        {r.trunk_did && <div className="col-span-2"><span className="text-muted-foreground">DID:</span> <span className="font-mono">{r.trunk_did}</span></div>}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Data/Ora</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Dir.</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Chiamante</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Destinazione</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Dest. Interna</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Trunk / DID</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Durata</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Esito</TableHead>
                    {!compact && <TableHead className="text-xs font-semibold uppercase tracking-wider">MOS</TableHead>}
                    <TableHead className="text-xs font-semibold uppercase tracking-wider w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((r) => (
                    <Fragment key={r.id}>
                      <TableRow className="hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => setExpandedRow(expandedRow === r.id ? null : r.id)}>
                        <TableCell className="text-sm whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3 text-muted-foreground opacity-60" />
                            {formatDateTime(r.start_time)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {getDirectionIcon(r.call_direction)}
                            <span className="text-xs font-medium">{getDirectionLabel(r.call_direction)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium text-sm font-mono">{r.caller_id_number}</div>
                            {r.caller_id_name && <div className="text-xs text-muted-foreground">{r.caller_id_name}</div>}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{r.destination_number}</TableCell>
                        <TableCell>{getDestTypeBadge(r)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{r.trunk_name || '-'}</span>
                            {r.trunk_did && <span className="text-xs text-blue-500 font-mono">{r.trunk_did}</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded text-xs">
                            {formatDuration(r.duration)}
                          </span>
                        </TableCell>
                        <TableCell>{getDispositionBadge(r.hangup_disposition)}</TableCell>
                        {!compact && <TableCell>{getMOSBadge(r.mos_score)}</TableCell>}
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            {expandedRow === r.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                      </TableRow>

                      {expandedRow === r.id && (
                        <TableRow className="bg-muted/20">
                          <TableCell colSpan={compact ? 9 : 10} className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">Call UUID</span>
                                <div className="font-mono text-xs text-foreground select-all break-all">{r.call_uuid}</div>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">Ora Risposta</span>
                                <div className="text-foreground text-xs">{r.answer_time ? formatDateTime(r.answer_time) : '-'}</div>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">Sec. Fatturati</span>
                                <div className="text-foreground text-xs">{r.bill_sec}s</div>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">Causa Riaggancio</span>
                                <div className="text-foreground text-xs">{r.hangup_cause}</div>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">Codec</span>
                                <div className="text-foreground text-xs">{r.read_codec || 'N/A'}</div>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">Gateway</span>
                                <div className="text-foreground text-xs">{r.gateway_name || 'N/A'}</div>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">SIP From Host</span>
                                <div className="text-foreground text-xs font-mono">{r.sip_from_host || 'N/A'}</div>
                              </div>
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-muted-foreground uppercase">MOS</span>
                                <div>{getMOSBadge(r.mos_score)}</div>
                              </div>

                              {(() => {
                                const meta = r.metadata;
                                const dt = meta?.destination_type;
                                if (!dt && !(r.call_direction === 'inbound' && r.destination_number && /^\d{3,5}$/.test(r.destination_number))) return null;

                                const effectiveType = dt || 'extension';
                                const isAI = ['ai_agent', 'internal_ai_agent', 'external_ai_agent'].includes(effectiveType);
                                const isQueue = effectiveType === 'queue';
                                const isIVR = effectiveType === 'ivr';
                                const isRingGroup = ['ring_all', 'ring_group'].includes(effectiveType);
                                const isExtension = effectiveType === 'extension';

                                const borderColor = isAI ? 'border-purple-500/30' :
                                  isQueue ? 'border-orange-500/30' :
                                  isIVR ? 'border-blue-500/30' :
                                  isRingGroup ? 'border-green-500/30' :
                                  'border-gray-500/30';
                                const bgColor = isAI ? 'bg-purple-500/5' :
                                  isQueue ? 'bg-orange-500/5' :
                                  isIVR ? 'bg-blue-500/5' :
                                  isRingGroup ? 'bg-green-500/5' :
                                  'bg-gray-500/5';
                                const iconColor = isAI ? 'text-purple-500' :
                                  isQueue ? 'text-orange-500' :
                                  isIVR ? 'text-blue-500' :
                                  isRingGroup ? 'text-green-500' :
                                  'text-gray-500';
                                const title = isAI ? 'Routing AI Agent' :
                                  isQueue ? 'Routing Coda' :
                                  isIVR ? 'Routing IVR' :
                                  isRingGroup ? 'Routing Ring Group' :
                                  'Routing Interno';

                                return (
                                  <div className={`col-span-2 md:col-span-4 mt-3 p-3 ${bgColor} border ${borderColor} rounded-lg space-y-2`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className={iconColor}>{getDestIcon(effectiveType)}</div>
                                      <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                      <div className="space-y-0.5">
                                        <span className="text-xs text-muted-foreground">Destinazione</span>
                                        <div className="text-xs font-medium text-foreground">
                                          {meta?.destination_name || meta?.destination_id || (isExtension && r.destination_number ? `Ext ${r.destination_number}` : '-')}
                                        </div>
                                      </div>

                                      {isQueue && (
                                        <>
                                          <div className="space-y-0.5">
                                            <span className="text-xs text-muted-foreground">Agente Risposta</span>
                                            <div className="text-xs font-medium text-foreground">
                                              {meta?.answered_by_extension ? (
                                                <span className="flex items-center gap-1">
                                                  <User className="h-3 w-3 text-green-500" />
                                                  Ext {meta.answered_by_extension}
                                                  {meta.answered_by_agent && <span className="text-muted-foreground ml-1">({meta.answered_by_agent.split('@')[0]})</span>}
                                                </span>
                                              ) : <span className="text-muted-foreground">Nessuno</span>}
                                            </div>
                                          </div>
                                          <div className="space-y-0.5">
                                            <span className="text-xs text-muted-foreground">Tempo Attesa</span>
                                            <div className="text-xs font-medium text-foreground">
                                              {meta?.cc_wait_seconds !== undefined ? `${meta.cc_wait_seconds}s` : '-'}
                                            </div>
                                          </div>
                                          <div className="space-y-0.5">
                                            <span className="text-xs text-muted-foreground">Tentativi</span>
                                            <div className="text-xs font-medium text-foreground">
                                              {meta?.events?.length || 0} agenti provati
                                            </div>
                                          </div>
                                        </>
                                      )}

                                      {isIVR && (
                                        <>
                                          <div className="space-y-0.5">
                                            <span className="text-xs text-muted-foreground">Trasferito a</span>
                                            <div className="text-xs font-medium text-foreground flex items-center gap-1">
                                              {meta?.transfer_destination ? (
                                                <><ArrowRight className="h-3 w-3 text-blue-500" /> {meta.transfer_destination}</>
                                              ) : meta?.answered_by_extension ? (
                                                <><ArrowRight className="h-3 w-3 text-blue-500" /> Ext {meta.answered_by_extension}</>
                                              ) : <span className="text-muted-foreground">-</span>}
                                            </div>
                                          </div>
                                          {meta?.ivr_selection && (
                                            <div className="space-y-0.5">
                                              <span className="text-xs text-muted-foreground">Selezione DTMF</span>
                                              <div className="text-xs font-medium text-foreground">{meta.ivr_selection}</div>
                                            </div>
                                          )}
                                        </>
                                      )}

                                      {isAI && (
                                        <>
                                          <div className="space-y-0.5">
                                            <span className="text-xs text-muted-foreground">Agente AI</span>
                                            <div className="text-xs font-medium text-foreground flex items-center gap-1">
                                              <Bot className="h-3 w-3 text-purple-500" />
                                              {meta?.destination_name || meta?.ai_agent_name || meta?.destination_id?.substring?.(0, 8) || '-'}
                                            </div>
                                          </div>
                                          {meta?.routing_mode && (
                                            <div className="space-y-0.5">
                                              <span className="text-xs text-muted-foreground">Modalità Routing</span>
                                              <div className="text-xs font-medium text-foreground">
                                                <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300">
                                                  {meta.routing_mode === 'direct' ? 'Diretto' :
                                                   meta.routing_mode === 'fallback' ? 'Fallback' :
                                                   meta.routing_mode === 'hybrid' ? 'Ibrido' : meta.routing_mode}
                                                </Badge>
                                              </div>
                                            </div>
                                          )}
                                          {meta?.transfer_destination && (
                                            <div className="space-y-0.5">
                                              <span className="text-xs text-muted-foreground">Trasferito a</span>
                                              <div className="text-xs font-medium text-foreground flex items-center gap-1">
                                                <ArrowRight className="h-3 w-3 text-purple-500" /> {meta.transfer_destination}
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      )}

                                      {isRingGroup && (
                                        <>
                                          <div className="space-y-0.5">
                                            <span className="text-xs text-muted-foreground">Risposto da</span>
                                            <div className="text-xs font-medium text-foreground">
                                              {meta?.answered_by_extension ? (
                                                <span className="flex items-center gap-1">
                                                  <User className="h-3 w-3 text-green-500" />
                                                  Ext {meta.answered_by_extension}
                                                </span>
                                              ) : <span className="text-muted-foreground">Nessuno</span>}
                                            </div>
                                          </div>
                                        </>
                                      )}

                                      {isExtension && !isQueue && !isIVR && !isAI && !isRingGroup && (
                                        <div className="space-y-0.5">
                                          <span className="text-xs text-muted-foreground">Interno chiamato</span>
                                          <div className="text-xs font-medium text-foreground flex items-center gap-1">
                                            <User className="h-3 w-3 text-gray-500" />
                                            Ext {meta?.destination_id || r.destination_number || '-'}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {isQueue && meta?.events && meta.events.length > 0 && (
                                      <div className="mt-3 pt-2 border-t border-border/50">
                                        <div className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-1.5 mb-2">
                                          <Clock className="h-3 w-3" />
                                          Timeline Tentativi ({meta.events.length})
                                        </div>
                                        <div className="relative border-l-2 border-muted-foreground/30 pl-3 space-y-2">
                                          {(meta.events as Array<{ disposition?: string; agent?: string; timestamp?: string; duration?: number; mos_score?: number }>).map((event, idx: number) => (
                                            <div key={idx} className="relative">
                                              <div className={`absolute -left-[15px] w-2.5 h-2.5 rounded-full border-2 ${
                                                event.disposition === 'answered' ? 'bg-green-500 border-green-500' :
                                                event.disposition === 'cancelled' || event.disposition === 'no_answer' ? 'bg-yellow-500 border-yellow-500' :
                                                'bg-gray-400 border-gray-400'
                                              }`} />
                                              <div className="bg-muted/50 rounded p-2 border border-border">
                                                <div className="flex items-center justify-between mb-0.5">
                                                  <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className={`text-xs ${
                                                      event.disposition === 'answered' ? 'bg-green-100 text-green-700 border-green-200' :
                                                      event.disposition === 'cancelled' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                                                      event.disposition === 'no_answer' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                                      'bg-gray-100 text-gray-700 border-gray-200'
                                                    }`}>
                                                      {event.disposition === 'answered' ? 'Risposta' :
                                                       event.disposition === 'cancelled' ? 'Cancellata' :
                                                       event.disposition === 'no_answer' ? 'Nessuna Risposta' :
                                                       event.disposition || 'Unknown'}
                                                    </Badge>
                                                    {event.agent && (
                                                      <span className="text-xs text-muted-foreground">
                                                        Agente: <span className="font-medium text-foreground">{event.agent.split('@')[0]}</span>
                                                      </span>
                                                    )}
                                                  </div>
                                                  {event.timestamp && (
                                                    <span className="text-xs text-muted-foreground">
                                                      {new Date(event.timestamp).toLocaleTimeString('it-IT')}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                  {(event.duration ?? 0) > 0 && <span>Durata: {formatDuration(event.duration ?? 0)}</span>}
                                                  {event.mos_score && (
                                                    <span>MOS: <span className={event.mos_score >= 4 ? 'text-green-600' : event.mos_score >= 3 ? 'text-yellow-600' : 'text-red-600'}>
                                                      {event.mos_score.toFixed(2)}
                                                    </span></span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {r.recording_path && (
                                <div className="col-span-2 md:col-span-4 mt-2">
                                  <div className="flex items-center gap-2 p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                    <Download className="h-4 w-4 text-blue-500" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-semibold text-blue-600 dark:text-blue-300">Registrazione disponibile</div>
                                      <div className="text-xs text-muted-foreground truncate font-mono">{r.recording_path}</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}

                  {records.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={compact ? 9 : 10} className="text-center py-12 text-muted-foreground">
                        <Search className="h-10 w-10 mx-auto mb-3 opacity-20" />
                        <p>Nessun record trovato per i filtri selezionati</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            </>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Pagina {page} di {totalPages} — {total} record totali
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs">
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Precedente
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs">
                  Successivo <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
